/*
 * Copyright 2023 StarTree Inc.
 *
 * All rights reserved. Confidential and proprietary information of StarTree Inc.
 */
import { Drawer } from "@material-ui/core";
import { default as classnames } from "classnames";
import { isNull } from "lodash";
import React, {
    createContext,
    FunctionComponent,
    useContext,
    useEffect,
    useState,
} from "react";
import { useLocation } from "react-router-dom";
import { DrawerContentV1 } from "./drawer-content-v1";
import { DrawerFooterV1 } from "./drawer-footer-v1";
import { DrawerHeaderV1 } from "./drawer-header-v1";
import {
    DrawerProviderV1ContextProps,
    DrawerProviderV1Props,
    DrawerV1Props,
} from "./drawer-provider-v1.interfaces";
import { useDrawerProviderV1Styles } from "./drawer-provider-v1.styles";

export const DrawerProviderV1: FunctionComponent<DrawerProviderV1Props> = ({
    children,
    className,
}) => {
    const classes = useDrawerProviderV1Styles();
    const location = useLocation();

    const [isOpen, setIsOpen] = useState(false);
    const [drawerData, setDrawerData] = useState<DrawerV1Props | null>(null);

    useEffect(() => {
        // hide drawer when navigating to different page
        closeDrawer();
    }, [location.pathname]);

    const closeDrawer = (): void => {
        drawerData?.onClose && drawerData.onClose();

        setIsOpen(false);
        setDrawerData(null);
    };

    const openDrawer = (drawerData: DrawerV1Props): void => {
        setIsOpen(true);
        setDrawerData(drawerData);
    };

    const drawerContext = {
        isOpen: isOpen,
        closeDrawer: closeDrawer,
        openDrawer: openDrawer,
    };

    // Render UI
    return (
        <DrawerProviderV1Context.Provider value={drawerContext}>
            {children}

            {!isNull(drawerData) && (
                <Drawer
                    anchor={drawerData.anchor || "right"}
                    className={classnames(className, "drawer-provider-v1")}
                    classes={{
                        paper: classes.root,
                    }}
                    open={isOpen}
                    // onClose={closeDrawer}
                >
                    {/* Header */}
                    {drawerData.title && (
                        <DrawerHeaderV1
                            handleClose={closeDrawer}
                            title={drawerData.title}
                        />
                    )}

                    {/* Content */}
                    <DrawerContentV1>{drawerData.contents}</DrawerContentV1>

                    {/* Actions */}
                    {drawerData.actions && (
                        <DrawerFooterV1>{drawerData.actions}</DrawerFooterV1>
                    )}
                </Drawer>
            )}
        </DrawerProviderV1Context.Provider>
    );
};

const DrawerProviderV1Context = createContext<DrawerProviderV1ContextProps>(
    {} as DrawerProviderV1ContextProps
);

export const useDrawerProviderV1 = (): DrawerProviderV1ContextProps => {
    return useContext(DrawerProviderV1Context);
};
